import "@szhsin/react-menu/dist/index.css";

import { Menu, MenuDivider, MenuItem } from "@szhsin/react-menu";

import DownCaret from "shared/components/utils/icons/DownCaret";
import { NavLinkText } from "./NavLink.styles";
import PropTypes from "prop-types";
import React from "react";
import { NavGroupTrigger, useNavGroupMenuStyles } from "./NavGroupMenu.styles";
import { useLocation } from "react-router-dom";

// Renders one collapsed-mode nav category as a dropdown: a trigger labelled with
// the group name plus a caret, opening a menu of its child links.
//
// - Internal links navigate via react-router (preserving ctrl/cmd/middle-click
//   to open in a new tab, matching NavLink behaviour).
// - External links render as plain anchors (Reporter, Provide Feedback).
// - The trigger is marked active when any internal child matches the location,
//   and a divider separates internal from external children.
const NavGroupMenu = ({ group, classesText, navigate, getIsActive }) => {
    const menuClasses = useNavGroupMenuStyles();
    const location = useLocation();

    const isChildActive = (child) => {
        if (child.kind !== "internal") {
            return false;
        }
        const customIsActive = getIsActive?.(child.route);
        return customIsActive
            ? customIsActive(location)
            : location.pathname.startsWith(child.route);
    };

    const groupActive = group.children.some(isChildActive);

    const handleInternalClick = (route) => (event) => {
        const nativeEvent = event.syntheticEvent;
        // Let the browser handle new-tab intents (ctrl/cmd/middle-click).
        if (
            nativeEvent &&
            (nativeEvent.ctrlKey ||
                nativeEvent.metaKey ||
                nativeEvent.button === 1)
        ) {
            return;
        }
        nativeEvent?.preventDefault?.();
        navigate(route);
    };

    const hasInternal = group.children.some((c) => c.kind === "internal");
    const hasExternal = group.children.some((c) => c.kind === "external");
    const needsDivider = hasInternal && hasExternal;

    const menuItems = [];
    let dividerRendered = false;
    group.children.forEach((child) => {
        // Divider between the internal links and the external ones.
        if (needsDivider && !dividerRendered && child.kind === "external") {
            dividerRendered = true;
            menuItems.push(<MenuDivider key={`${group.key}-divider`} />);
        }

        if (child.kind === "external") {
            menuItems.push(
                <MenuItem
                    key={child.id}
                    href={child.href}
                    target={child.target}
                    rel={child.rel}
                >
                    {child.name}
                </MenuItem>
            );
        } else {
            menuItems.push(
                <MenuItem
                    key={child.id}
                    href={child.route}
                    onClick={handleInternalClick(child.route)}
                    className={
                        isChildActive(child)
                            ? "rc-menu__item--active"
                            : undefined
                    }
                >
                    {child.name}
                </MenuItem>
            );
        }
    });

    return (
        <Menu
            portal
            align="start"
            offsetY={6}
            className={menuClasses.menu}
            menuButton={({ open }) => (
                <NavGroupTrigger type="button" data-open={open}>
                    <NavLinkText
                        variant="smallLight"
                        $active={groupActive}
                        className={classesText.borderBottom}
                    >
                        {group.label}
                    </NavLinkText>
                    <DownCaret size="16" />
                </NavGroupTrigger>
            )}
        >
            {menuItems}
        </Menu>
    );
};

NavGroupMenu.propTypes = {
    group: PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        children: PropTypes.array.isRequired,
    }).isRequired,
    classesText: PropTypes.object,
    navigate: PropTypes.func,
    getIsActive: PropTypes.func,
};

export default NavGroupMenu;
